// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import { getAssetBrandLogoUrl } from '@shared/data-access/utils';
import type { HistValue } from '@shared/ui-kit';
import type { AssetLogoType } from '@shared/ui-kit/components';

const TOP_POSITIONS_LIMIT = 10;
const ASSET_TYPES: Record<AssetLogoType, string[]> = {
  stock: ['common stock', 'stock'],
  etf: ['etf', 'etp'],
  mutual_fund: [
    'mutual_fund',
    'mutual fund',
    'fund',
    'pension-plan',
    'pension-plans',
    'eu_pension_plans',
    'us_pension_plans',
  ],
  crypto: ['crypto'],
};

const RETURN_PERIOD_BY_HIST: Record<
  Extract<HistValue, '1M' | '3M' | '6M' | 'YTD' | '1Y'>,
  'm1_return' | 'm3_return' | 'm6_return' | 'ytd_return' | 'y1_return'
> = {
  '1M': 'm1_return',
  '3M': 'm3_return',
  '6M': 'm6_return',
  YTD: 'ytd_return',
  '1Y': 'y1_return',
};

const getAssetLogoType = (assetType?: string | null): AssetLogoType | undefined => {
  if (!assetType) {
    return undefined;
  }

  const normalizedAssetType = assetType.toLowerCase();

  return (Object.keys(ASSET_TYPES) as AssetLogoType[]).find(type =>
    ASSET_TYPES[type].includes(normalizedAssetType),
  );
};

const getReturnPeriod = (activePeriod: HistValue) => {
  if (activePeriod in RETURN_PERIOD_BY_HIST) {
    return RETURN_PERIOD_BY_HIST[
      activePeriod as Extract<HistValue, '1M' | '3M' | '6M' | 'YTD' | '1Y'>
    ];
  }

  return RETURN_PERIOD_BY_HIST['3M'];
};

const getPeriodReturn = (
  details: AccountsHoldingsData['details'],
  activePeriod: HistValue,
) => {
  if (!details) {
    return 0;
  }

  const returnPeriod = getReturnPeriod(activePeriod);
  const detailsWithReturns = details as AccountsHoldingsData['details'] & {
    returns?: Record<string, number | null | undefined>;
    [key: string]: unknown;
  };

  const directReturn = detailsWithReturns[returnPeriod];
  if (typeof directReturn === 'number' && Number.isFinite(directReturn)) {
    return directReturn;
  }

  const nestedReturn = detailsWithReturns.returns?.[returnPeriod];
  if (typeof nestedReturn === 'number' && Number.isFinite(nestedReturn)) {
    return nestedReturn;
  }

  return 0;
};

export const buildTopPositions = (
  positions: AccountsHoldingsData[],
  activePeriod: HistValue,
) => {
  const mappedPositions = positions.map(position => {
    const periodReturn = getPeriodReturn(position.details, activePeriod);

    return {
      id:
        position.id?.toString() ??
        `${position.iban}-${position.ticker ?? position.isin ?? position.name}`,
      investment: position.investment,
      assetType: getAssetLogoType(
        position.details?.asset_type ?? position.asset_type,
      ),
      logoUrl: getAssetBrandLogoUrl(position.details),
      name: position.name,
      periodReturn,
      periodReturnInvestment: position.investment * periodReturn,
      symbol: position.ticker ?? position.isin ?? '-',
    };
  });

  mappedPositions.sort((firstPosition, secondPosition) => {
    if (secondPosition.periodReturn !== firstPosition.periodReturn) {
      return secondPosition.periodReturn - firstPosition.periodReturn;
    }

    return secondPosition.investment - firstPosition.investment;
  });

  if (mappedPositions.length > TOP_POSITIONS_LIMIT) {
    mappedPositions.length = TOP_POSITIONS_LIMIT;
  }

  return mappedPositions.map((position, index) => {
    return {
      ...position,
      order: index + 1,
    };
  });
};
